'use client';

import { motion } from 'framer-motion';
import { Rocket, ArrowDown } from '@phosphor-icons/react';

interface HeroSectionProps {
  onExplore: () => void;
}

export default function HeroSection({ onExplore }: HeroSectionProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black px-4 sm:px-6 lg:px-8"
    >
      <div className="text-center max-w-7xl w-full">
        {/* Logo/Título Principal */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.5, duration: 1, type: "spring" }}
          className="mb-6 sm:mb-8 lg:mb-12"
        >
          <div className="flex justify-center items-center mb-4 sm:mb-6">
            <img
              src="/logo.svg"
              alt="Nexuniverse Logo"
              className="w-24 sm:w-32 md:w-40 lg:w-48 xl:w-56 h-auto"
            />
          </div>
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black tracking-wider text-white mb-2 sm:mb-4">
            NEXUniverse
          </h1>
          <div className="w-20 sm:w-24 md:w-32 h-px bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-2 sm:mb-4" />
          <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-light text-gray-300 tracking-widest">
            Desenvolvimento Digital
          </p>
        </motion.div>

        {/* Subtítulo */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-400 font-light mb-8 sm:mb-10 lg:mb-12 leading-relaxed max-w-4xl mx-auto px-4"
        >
          Explore o cosmos dos nossos projetos digitais.<br className="hidden sm:block" />
          <span className="block sm:inline"> Cada universo representa uma jornada única de inovação e tecnologia.</span>
        </motion.p>

        {/* Estatísticas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="flex flex-col sm:flex-row justify-center items-center space-y-6 sm:space-y-0 sm:space-x-8 md:space-x-12 mb-12 sm:mb-14 lg:mb-16"
        >
          <div className="text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">5+</div>
            <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider">Universos</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">20+</div>
            <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider">Tecnologias</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">∞</div>
            <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider">Possibilidades</div>
          </div>
        </motion.div>

        {/* Botão de Explorar */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.8 }}
          onClick={onExplore}
          className="group relative px-8 sm:px-10 lg:px-12 py-3 sm:py-4 border border-white/20 rounded-full hover:border-white/40 transition-all duration-500 overflow-hidden min-h-[48px] touch-manipulation"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative flex items-center justify-center space-x-2 sm:space-x-3 text-white">
            <Rocket size={18} weight="light" className="sm:w-5 sm:h-5" />
            <span className="font-medium tracking-wider text-sm sm:text-base">EXPLORAR UNIVERSOS</span>
            <ArrowDown size={14} weight="light" className="sm:w-4 sm:h-4 group-hover:translate-y-1 transition-transform duration-300" />
          </div>
        </motion.button>

        {/* Indicador de Scroll */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 1 }}
          className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-5 sm:w-6 h-8 sm:h-10 border border-white/20 rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              className="w-0.5 sm:w-1 h-2 sm:h-3 bg-white/40 rounded-full mt-1.5 sm:mt-2"
            />
          </motion.div>
        </motion.div>
      </div>

      {/* Efeitos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Círculos concêntricos */}
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.1 }}
            transition={{ delay: 1 + i * 0.3, duration: 2 }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border border-white rounded-full"
            style={{
              width: `${200 + i * 150}px`,
              height: `${200 + i * 150}px`,
            }}
          />
        ))}

        {/* Linhas de grade - ocultas em mobile */}
        <div className="absolute inset-0 opacity-5 hidden md:block">
          <div className="grid grid-cols-8 md:grid-cols-12 grid-rows-8 md:grid-rows-12 h-full w-full">
            {[...Array(96)].map((_, i) => (
              <div key={i} className="border border-white/10" />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}